import { Injectable } from '@angular/core';
import { SecureKeyManagerService } from './secure-key-manager-service';
import { environment } from '../../../environments/environment';
import { AES, PBKDF2, SHA256, lib, enc, mode, pad, algo } from 'crypto-js';

export interface EncryptionResult {
  encryptedData: string;
  salt: string;
  iv: string;
  keyId: string;
  timestamp: number;
}

export interface SecurePayload {
  data: string;
  metadata: {
    algorithm: string;
    keyDerivation: string;
    iterations: number;
    keyLength: number;
    timestamp: number;
    deviceFingerprint: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SecureEncryptionService {
  private readonly ALGORITHM = 'AES-256-CBC';
  private readonly KEY_DERIVATION = 'PBKDF2';
  private readonly PBKDF2_ITERATIONS = environment.pbkdf2Iterations || 100000; // NIST recommended minimum
  private readonly KEY_LENGTH = 256; // bits
  private readonly SALT_LENGTH = 32; // bytes
  private readonly IV_LENGTH = 16; // bytes

  constructor(private keyManager: SecureKeyManagerService) {}

  /**
   * Encrypt password with enhanced security
   */
  async encryptPassword(password: string, userContext?: string): Promise<EncryptionResult> {
    try {
      
      // Generate salt and IV
      const salt = lib.WordArray.random(256/8);
    const iv = lib.WordArray.random(128/8);
      
      // Get or generate encryption key
     // Get or generate encryption key
      const masterKey = this.keyManager.getCurrentMasterKeySync();
      
      if (!masterKey) {
        throw new Error('Failed to obtain encryption key');
      }
      
      const encryptionKey = masterKey.key;
      
      if (!encryptionKey) {
        throw new Error('Failed to obtain encryption key');
      }
      
      // Derive key using PBKDF2
      const derivedKey = PBKDF2(encryptionKey, salt, {
        keySize: 256/32,
        iterations: this.PBKDF2_ITERATIONS
      });
      
      // Encrypt password using AES-256-CBC
      const encrypted = AES.encrypt(password, derivedKey, {
        iv: iv,
        mode: mode.CBC,
        padding: pad.Pkcs7
      });
      
      
      return {
        encryptedData: encrypted.toString(),
        salt: enc.Base64.stringify(salt),
        iv: enc.Base64.stringify(iv),
        keyId: masterKey.id,
        timestamp: Date.now()
      };
      
    } catch (error) {
      console.error('❌ Password encryption failed:', error);
      throw new Error(`Password encryption failed: ${error}`);
    }
  }

  /**
   * Create secure payload for transmission
   */
  public async createSecurePayload(data: any): Promise<SecurePayload> {
    const encryptionResult = await this.encryptPassword(JSON.stringify(data));
    
    return {
      data: JSON.stringify(encryptionResult),
      metadata: {
        algorithm: this.ALGORITHM,
        keyDerivation: this.KEY_DERIVATION,
        iterations: this.PBKDF2_ITERATIONS,
        keyLength: this.KEY_LENGTH,
        timestamp: Date.now(),
        deviceFingerprint: this.generateDeviceFingerprint()
      }
    };
  }

  /**
   * Derive encryption key using PBKDF2
   */
  private deriveKey(masterKey: string, salt: string): string {
    return PBKDF2(masterKey, enc.Hex.parse(salt), {
      keySize: this.KEY_LENGTH / 32, // Convert bits to words
      iterations: this.PBKDF2_ITERATIONS,
      hasher: algo.SHA256
    }).toString();
  }

  /**
   * Generate cryptographically secure salt
   */
  private generateSalt(): string {
    return lib.WordArray.random(this.SALT_LENGTH).toString();
  }

  /**
   * Generate initialization vector
   */
  private generateIV(): string {
    return lib.WordArray.random(this.IV_LENGTH).toString();
  }

  /**
   * Generate device fingerprint for additional security
   */
  private generateDeviceFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }
    
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL(),
      webgl: this.getWebGLFingerprint()
    };

    return SHA256(JSON.stringify(fingerprint)).toString();
  }

  /**
   * Get WebGL fingerprint for device identification
   */
  private getWebGLFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
      if (!gl) return 'no-webgl';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return 'no-debug-info';

      return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) + '~' + 
             gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    } catch (error) {
      return 'webgl-error';
    }
  }

  /**
   * Validate encryption parameters
   */
  public validateEncryptionParams(result: EncryptionResult): boolean {
    return !!(
      result.encryptedData &&
      result.salt &&
      result.iv &&
      result.keyId &&
      result.timestamp &&
      result.salt.length === this.SALT_LENGTH * 2 && // Hex string is 2x length
      result.iv.length === this.IV_LENGTH * 2
    );
  }

  /**
   * Get encryption metadata for compliance
   */
  public getEncryptionMetadata(): any {
    return {
      algorithm: this.ALGORITHM,
      keyDerivation: this.KEY_DERIVATION,
      iterations: this.PBKDF2_ITERATIONS,
      keyLength: this.KEY_LENGTH,
      saltLength: this.SALT_LENGTH,
      ivLength: this.IV_LENGTH,
      compliance: ['NIST-800-63B', 'FIPS-140-2', 'PCI-DSS']
    };
  }

  /**
   * Secure memory cleanup (best effort)
   */
  public secureCleanup(sensitiveData: string): void {
    // Overwrite the string in memory (best effort in JavaScript)
    if (sensitiveData) {
      // Create new string with random data of same length
      const randomData = lib.WordArray.random(sensitiveData.length).toString();
      sensitiveData = randomData;
    }
  }

  /**
   * Encrypt sensitive data like tokens
   */
  public encryptSensitiveData(data: string): string {
    try {
      const masterKey = this.keyManager.getCurrentMasterKeySync();
      if (!masterKey) {
        throw new Error('Master key not available');
      }

      const salt = this.generateSalt();
      const iv = this.generateIV();
      const derivedKey = this.deriveKey(masterKey.key, salt);

      const encrypted = AES.encrypt(data, derivedKey, {
      iv: enc.Hex.parse(iv),
      mode: mode.CBC,
      padding: pad.Pkcs7
    });

      return JSON.stringify({
        data: encrypted.toString(),
        salt: salt,
        iv: iv,
        keyId: masterKey.id
      });
    } catch (error) {
      console.error('Sensitive data encryption failed:', error);
      throw new Error('Encryption failed');
    }
  }

  /**
   * Clear all encryption data from memory and storage
   */
  public clearEncryptionData(): void {
    try {
      // Clear any cached encryption keys
      this.keyManager.clearAllKeys();
      
      // Clear session storage items related to encryption
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('secure_key_') || key.startsWith('login_attempts_') || key.includes('encrypted'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // Clear localStorage items as well
      const localKeysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('secure_key_') || key.startsWith('login_attempts_') || key.includes('encrypted'))) {
          localKeysToRemove.push(key);
        }
      }
      
      localKeysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {}
  }

  /**
   * Check if key rotation is needed
   */
  public shouldRotateKey(): boolean {
    try {
      const currentKey = this.keyManager.getCurrentMasterKeySync();
      if (!currentKey) {
        return true; // No key exists, need to generate one
      }

      // Ensure createdAt is a Date object
      let createdAt: Date;
      if (currentKey.createdAt instanceof Date) {
        createdAt = currentKey.createdAt;
      } else {
        // Convert string to Date if needed
        createdAt = new Date(currentKey.createdAt);
      }

      // Validate that we have a valid Date
      if (isNaN(createdAt.getTime())) {
        console.warn('Invalid createdAt date in key, forcing rotation');
        return true;
      }

      const keyAge = Date.now() - createdAt.getTime();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours

      return keyAge > maxAge;
    } catch (error) {
      console.error('Error checking key rotation status:', error);
      return true; // Default to rotation needed on error
    }
  }

  /**
   * Get current key information
   */
  public getCurrentKeyInfo(): any {
    try {
      const currentKey = this.keyManager.getCurrentMasterKeySync();
      if (!currentKey) {
        return null;
      }

      return {
        keyId: currentKey.id,
        version: currentKey.version,
        createdAt: currentKey.createdAt,
        expiresAt: currentKey.expiresAt,
        isActive: currentKey.isActive,
        algorithm: this.ALGORITHM,
        keyLength: this.KEY_LENGTH
      };
    } catch (error) {
      console.error('Error getting current key info:', error);
      return null;
    }
  }
}